import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService } from '../../../_services/alert.service';
import { DeliveryValidationService, PurchaseReceipt } from '../../../_services/delivery-validation.service';
import { ContainerCard, ContainerReceiptCardComponent, toContainerCard } from '../container-receipt/container-receipt-card.component';

interface ReceiptCard extends PurchaseReceipt {
  // Units being booked in now, per line id - defaults to nothing until the
  // team types what actually came off the lorry (or hits "Receive all").
  receiveNow: Record<number, number>;
  saving: boolean;
}


type Yard = 'ALL' | 'Kettering' | 'Markstey';

// Goods arriving at the yards. Shipped goods are checked in container by
// container once a container is Delivered (PurchaseShipmentsService.
// receiveContainer books its stock into each PO it carries and marks it
// Received). Purchase orders that aren't on a shipment - local suppliers
// delivering by lorry - are still booked in by PO (PurchaseOrdersService.
// receiveLines).
@Component({
    selector: 'app-purchase-receipts',
    imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, DatePipe, ContainerReceiptCardComponent],
    templateUrl: './purchase-receipts.component.html'
})
export class PurchaseReceiptsComponent implements OnInit {
  @Output() changed = new EventEmitter<void>();

  containers: ContainerCard[] = [];
  receipts: ReceiptCard[] = [];
  loading = false;
  yardFilter: Yard = 'ALL';
  // Kettering is hidden from the filter for now, like on Inventory - its
  // receipts still show under "All yards". Add 'Kettering' back to show it.
  readonly yards: Yard[] = ['ALL', 'Markstey'];

  constructor(
    private deliveryValidationService: DeliveryValidationService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  // `quiet` refreshes in the background (after booking something in) without
  // blanking the screen with a spinner.
  load(quiet = false): void {
    if (!quiet) this.loading = true;
    let pending = 2;
    const done = () => {
      if (--pending === 0) this.loading = false;
    };
    this.deliveryValidationService.getContainerReceipts().subscribe({
      next: (containers) => {
        // Keep what's being typed on cards that are still there.
        // Keep what's being typed on cards that are still there.
        const typed = new Map(this.containers.map((c) => [c.id, c]));
        this.containers = containers.map((c) => toContainerCard(c, typed.get(c.id)));
        done();
      },
      error: (error) => {
        done();
        this.alertService.alert(error?.error?.message || 'Failed to load delivered containers', 'error');
      },
    });
    this.deliveryValidationService.getReceipts().subscribe({
      next: (receipts) => {
        const typed = new Map(this.receipts.map((r) => [r.id, r.receiveNow]));
        this.receipts = receipts.map((r) => ({ ...r, receiveNow: typed.get(r.id) ?? {}, saving: false }));
        done();
      },
      error: (error) => {
        done();
        this.alertService.alert(error?.error?.message || 'Failed to load incoming stocks', 'error');
      },
    });
  }

  private inYard<T extends { store: string }>(list: T[]): T[] {
    return this.yardFilter === 'ALL' ? list : list.filter((r) => r.store === this.yardFilter);
  }

  get visibleContainers(): ContainerCard[] {
    return this.inYard(this.containers);
  }

  get visibleReceipts(): ReceiptCard[] {
    return this.inYard(this.receipts);
  }

  countFor(yard: Yard): number {
    const match = (store: string) => yard === 'ALL' || store === yard;
    return this.containers.filter((c) => match(c.store)).length + this.receipts.filter((r) => match(r.store)).length;
  }

  // A container was checked in: gone straight away, lists refresh quietly.
  onContainerReceived(c: ContainerCard): void {
    this.containers = this.containers.filter((x) => x !== c);
    this.load(true);
    this.changed.emit();
  }

  // ---- Direct deliveries (POs not on a shipment) ----
  outstandingUnits(receipt: ReceiptCard): number {
    return receipt.items.reduce((sum, i) => sum + i.outstanding, 0);
  }

  unitsToReceive(receipt: ReceiptCard): number {
    return Object.values(receipt.receiveNow).reduce((sum, q) => sum + (Number(q) || 0), 0);
  }

  receiveAll(receipt: ReceiptCard): void {
    receipt.items.forEach((item) => (receipt.receiveNow[item.id] = item.outstanding));
  }

  clearQuantities(receipt: ReceiptCard): void {
    receipt.receiveNow = {};
  }

  // Due date colouring: overdue red, due today amber, upcoming/none neutral.
  dueClass(receipt: ReceiptCard): string {
    if (!receipt.expectedDeliveryDate) return 'bg-gray-100 text-gray-500';
    const due = new Date(receipt.expectedDeliveryDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due.getTime() < today.getTime()) return 'bg-red-100 text-red-700';
    if (due.getTime() === today.getTime()) return 'bg-amber-100 text-amber-700';
    return 'bg-green-50 text-green-700';
  }

  bookIn(receipt: ReceiptCard): void {
    const lines = receipt.items
      .map((item) => ({ itemId: item.id, quantity: Number(receipt.receiveNow[item.id]) || 0 }))
      .filter((l) => l.quantity > 0);
    if (!lines.length) {
      this.alertService.alert('Enter how many units arrived on at least one line', 'error');
      return;
    }
    receipt.saving = true;
    this.deliveryValidationService.receiveReceipt(receipt.id, lines).subscribe({
      next: () => {
        receipt.saving = false;
        const units = lines.reduce((sum, l) => sum + l.quantity, 0);
        this.alertService.alert(`${units} unit${units === 1 ? '' : 's'} booked into ${receipt.store} stock`, 'success');
        receipt.receiveNow = {};
        this.load(true);
        this.changed.emit();
      },
      error: (error) => {
        receipt.saving = false;
        this.alertService.alert(error?.error?.message || 'Failed to book in goods', 'error');
      },
    });
  }
}
